import type { TenantSummary } from "./types";

export const recentTenantStorageKeyPrefix = "kn-recent-tenants:";
export const maxRecentTenants = 5;

export interface RecentTenantStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface OrderedTenants {
  recent: TenantSummary[];
  other: TenantSummary[];
}

export function getRecentTenantStorageKey(userId: string): string {
  return `${recentTenantStorageKeyPrefix}${encodeURIComponent(userId)}`;
}

export function readRecentTenantIds(
  userId: string,
  storage: RecentTenantStorage = window.localStorage
): string[] {
  try {
    const stored = storage.getItem(getRecentTenantStorageKey(userId));
    if (!stored) {
      return [];
    }

    const parsed: unknown = JSON.parse(stored);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter((tenantId): tenantId is string => typeof tenantId === "string" && tenantId.length > 0)
      .filter((tenantId, index, tenantIds) => tenantIds.indexOf(tenantId) === index)
      .slice(0, maxRecentTenants);
  } catch {
    return [];
  }
}

export function recordRecentTenant(
  userId: string,
  tenantId: string,
  storage: RecentTenantStorage = window.localStorage
): string[] {
  const nextRecentTenantIds = [
    tenantId,
    ...readRecentTenantIds(userId, storage).filter((recentTenantId) => recentTenantId !== tenantId)
  ].slice(0, maxRecentTenants);

  try {
    storage.setItem(getRecentTenantStorageKey(userId), JSON.stringify(nextRecentTenantIds));
  } catch {
    // Recent tenant history is a convenience and must not block tenant selection.
  }

  return nextRecentTenantIds;
}

export function orderTenants(
  tenants: ReadonlyArray<TenantSummary>,
  recentTenantIds: ReadonlyArray<string>
): OrderedTenants {
  const tenantsById = new Map(tenants.map((tenant) => [tenant.id, tenant]));
  const recent = recentTenantIds.flatMap((tenantId) => {
    const tenant = tenantsById.get(tenantId);
    return tenant ? [tenant] : [];
  });
  const recentTenantIdSet = new Set(recent.map((tenant) => tenant.id));
  const other = tenants
    .filter((tenant) => !recentTenantIdSet.has(tenant.id))
    .sort(compareTenants);

  return { recent, other };
}

function compareTenants(left: TenantSummary, right: TenantSummary): number {
  const nameComparison = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
  return nameComparison === 0
    ? left.id.localeCompare(right.id, undefined, { sensitivity: "base" })
    : nameComparison;
}
